import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import {getAuth, onAuthStateChanged} from 'firebase/auth';
import {useNavigate} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faBuilding,
  faBullseye,
  faGlobe,
  faHandshake,
  faSliders,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import './dash.css';
import {logAdminActivity} from '../utils/activityLogger';
import {
  DEFAULT_GROWTH_CONFIG,
  GROWTH_CATEGORIES,
  fetchGrowthScoutWithAuth,
  formatGrowthDate,
  formatGrowthDateTime,
  formatGrowthLabel,
} from './growthScoutShared';

function GrowthScoutComp() {
  const auth = getAuth();
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(Boolean(auth.currentUser));
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [runs, setRuns] = useState([]);
  const [config, setConfig] = useState(DEFAULT_GROWTH_CONFIG);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [priority, setPriority] = useState('');
  const deferredSearch = useDeferredValue(search);

  const latestRun = runs[0] || null;
  const summary = useMemo(() => ({
    total: opportunities.length,
    highPriority: opportunities.filter((item) => item.priority === 'high').length,
    kenyaStrong: opportunities.filter((item) => Number(item.kenyaRelevanceScore || 0) >= 20).length,
    linkedin: opportunities.filter((item) => item.sourceType === 'linkedin').length,
  }), [opportunities]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(Boolean(user));
    });
    return () => unsubscribe();
  }, [auth]);

  const loadConfig = useCallback(async () => {
    const result = await fetchGrowthScoutWithAuth(auth, '/config');
    setConfig((current) => ({
      ...current,
      ...(result.config || {}),
    }));
  }, [auth]);

  const loadData = useCallback(async ({silent = false} = {}) => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({limit: '120'});
      if (deferredSearch.trim()) params.set('search', deferredSearch.trim());
      if (category) params.set('category', category);
      if (sourceType) params.set('sourceType', sourceType);
      if (priority) params.set('priority', priority);

      const [opportunityData, runData] = await Promise.all([
        fetchGrowthScoutWithAuth(auth, `/opportunities?${params.toString()}`),
        fetchGrowthScoutWithAuth(auth, '/runs'),
      ]);
      setOpportunities(Array.isArray(opportunityData.opportunities) ? opportunityData.opportunities : []);
      setRuns(Array.isArray(runData.runs) ? runData.runs : []);
    } catch (error) {
      console.error('Failed to load Growth Scout:', error);
      if (!silent) {
        swal('Could not load Growth Scout', error.message || 'Please try again.', 'error');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [auth, category, deferredSearch, priority, sourceType]);

  useEffect(() => {
    if (!authReady) return;
    loadData();
    loadConfig().catch((error) => {
      console.error('Failed to load Growth Scout config:', error);
    });
  }, [authReady, loadConfig, loadData]);

  const handleCategoryToggle = (value) => {
    setConfig((current) => {
      const categories = current.categories.includes(value) ?
        current.categories.filter((item) => item !== value) :
        [...current.categories, value];
      return {...current, categories};
    });
  };

  const handleSaveConfig = async () => {
    if (config.categories.length === 0) {
      swal('Choose a category', 'Select at least one growth opportunity category.', 'warning');
      return;
    }

    setSavingConfig(true);
    try {
      const result = await fetchGrowthScoutWithAuth(auth, '/config', {
        method: 'PUT',
        body: config,
      });
      setConfig((current) => ({...current, ...(result.config || {})}));
      await logAdminActivity({
        action: 'Saved Growth Scout Config',
        details: config.categories.join(', '),
        meta: {source: 'growth-scout'},
      });
      setConfigOpen(false);
      swal('Growth Scout configured', 'The saved settings will be reused for manual and scheduled runs.', 'success');
    } catch (error) {
      console.error('Failed to save Growth Scout config:', error);
      swal('Could not save config', error.message || 'Please try again.', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const result = await fetchGrowthScoutWithAuth(auth, '/run', {
        method: 'POST',
        body: {},
      });
      await logAdminActivity({
        action: 'Ran Growth Opportunity Scout',
        details: result.summary || 'Growth Scout completed',
        meta: {
          source: 'growth-scout',
          runId: result.runId || '',
          createdCount: result.createdCount || 0,
        },
      });
      await loadData({silent: true});
      const sourceErrors = Array.isArray(result.sourceErrors) ? result.sourceErrors : [];
      if (sourceErrors.length > 0) {
        const firstError = sourceErrors[0];
        swal(
          'Scan completed with source issues',
          `${result.summary || 'The scan finished.'}\n\n${formatGrowthLabel(firstError.sourceType)}: ${firstError.message}`,
          'warning',
        );
        return;
      }
      if (Number(result.foundCount || 0) === 0) {
        swal(
          'No opportunities found',
          result.summary || 'The sources completed but returned no qualifying opportunities.',
          'warning',
        );
        return;
      }
      swal(
        'Growth scan complete',
        result.summary || `Saved ${result.createdCount || 0} new opportunities.`,
        'success',
      );
    } catch (error) {
      console.error('Failed to run Growth Scout:', error);
      swal('Could not run Growth Scout', error.message || 'Please try again.', 'error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className='dashComp opportunityScoutPage'>
      <section className='opportunityScoutTopBar'>
        <div className='opportunityScoutTopBarTitle'>
          <h2>Opportunity Scout</h2>
          <p>
            Find Kenya-relevant partnerships, competitions, funding signals, and acquisition leads from the open web and LinkedIn.
          </p>
          {latestRun && (
            <div className='opportunityScoutRunStamp'>
              Last scan: {formatGrowthDateTime(latestRun.completedAtMs || latestRun.updatedAtMs)}
            </div>
          )}
        </div>
        <div className='opportunityScoutTopBarActions'>
          <Button variant='dark' className='opportunityScoutPrimaryButton' onClick={handleRun} disabled={running}>
            {running ? <Spinner animation='border' size='sm' /> : <FontAwesomeIcon icon={faBolt} />}
            <span>{running ? 'Running...' : 'Run'}</span>
          </Button>
          <Button
            variant='outline-dark'
            className='opportunityScoutSecondaryButton'
            onClick={() => setConfigOpen(true)}
          >
            <FontAwesomeIcon icon={faSliders} />
            <span>Search Config</span>
          </Button>
        </div>
      </section>

      <section className='opportunityScoutSummaryGrid'>
        <article className='opportunityScoutSummaryCard'>
          <div className='opportunityScoutSummaryIcon opportunityScoutSummaryIcon--blue'>
            <FontAwesomeIcon icon={faHandshake} />
          </div>
          <div><p>Visible opportunities</p><h3>{summary.total}</h3></div>
        </article>
        <article className='opportunityScoutSummaryCard'>
          <div className='opportunityScoutSummaryIcon opportunityScoutSummaryIcon--rose'>
            <FontAwesomeIcon icon={faTrophy} />
          </div>
          <div><p>High priority</p><h3>{summary.highPriority}</h3></div>
        </article>
        <article className='opportunityScoutSummaryCard'>
          <div className='opportunityScoutSummaryIcon opportunityScoutSummaryIcon--green'>
            <FontAwesomeIcon icon={faBullseye} />
          </div>
          <div><p>Strong Kenya fit</p><h3>{summary.kenyaStrong}</h3></div>
        </article>
        <article className='opportunityScoutSummaryCard'>
          <div className='opportunityScoutSummaryIcon opportunityScoutSummaryIcon--gold'>
            <FontAwesomeIcon icon={faGlobe} />
          </div>
          <div><p>LinkedIn signals</p><h3>{summary.linkedin}</h3></div>
        </article>
      </section>

      <section className='opportunityScoutPanel'>
        <div className='opportunityScoutPanelHeader'>
          <div>
            <h4>Growth opportunities</h4>
            <p>Click any row to open the full opportunity details and source information.</p>
          </div>
          {latestRun && (
            <div className='opportunityScoutRunMeta'>
              <span className={`opportunityScoutStatusBadge opportunityScoutStatusBadge--${latestRun.status || 'new'}`}>
                {formatGrowthLabel(latestRun.status || 'completed')}
              </span>
              <small>{latestRun.createdCount || 0} new in last scan</small>
            </div>
          )}
        </div>

        <div className='growthScoutFilters'>
          <Form.Control
            type='search'
            placeholder='Search opportunity, organization, or source'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Form.Select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value=''>All categories</option>
            {GROWTH_CATEGORIES.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </Form.Select>
          <Form.Select value={sourceType} onChange={(event) => setSourceType(event.target.value)}>
            <option value=''>All sources</option>
            <option value='web'>Open web</option>
            <option value='linkedin'>LinkedIn</option>
          </Form.Select>
          <Form.Select value={priority} onChange={(event) => setPriority(event.target.value)}>
            <option value=''>All priorities</option>
            <option value='high'>High priority</option>
            <option value='medium'>Medium priority</option>
            <option value='low'>Low priority</option>
          </Form.Select>
        </div>

        {loading ? (
          <div className='opportunityScoutLoadingState'><Spinner animation='border' /></div>
        ) : opportunities.length === 0 ? (
          <div className='opportunityScoutEmptyState'>
            <h5>No growth opportunities yet</h5>
            <p>Run the first scan or adjust the filters to see more signals.</p>
          </div>
        ) : (
          <div className='growthScoutTableWrap'>
            <table className='table opportunityScoutTable growthScoutTable'>
              <thead>
                <tr>
                  <th>Opportunity</th>
                  <th>Category</th>
                  <th>Source</th>
                  <th>Kenya fit</th>
                  <th>Growth score</th>
                  <th>Priority</th>
                </tr>
              </thead>
              <tbody>
                {opportunities.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/growth-opportunity-scout/${item.id}`, {state: {opportunity: item}})}
                  >
                    <td>
                      <div className='growthScoutOpportunityCell'>
                        <strong>{item.title || 'Untitled opportunity'}</strong>
                        <span><FontAwesomeIcon icon={faBuilding} /> {item.organization || 'Unknown organization'}</span>
                        <small>{formatGrowthDate(item.publishedAtMs || item.createdAtMs)}</small>
                      </div>
                    </td>
                    <td>{item.categoryLabel || formatGrowthLabel(item.category)}</td>
                    <td>{formatGrowthLabel(item.sourceType)}</td>
                    <td><strong>{item.kenyaRelevanceScore || 0}/30</strong></td>
                    <td><strong>{item.growthScore || 0}/100</strong></td>
                    <td>{formatGrowthLabel(item.priority)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        show={configOpen}
        onHide={() => setConfigOpen(false)}
        centered
        size='xl'
        dialogClassName='opportunityScoutConfigModal'
        contentClassName='opportunityScoutConfigModalContent'
      >
        <Modal.Header closeButton className='opportunityScoutConfigModalHeader'>
          <Modal.Title>Growth Scout Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body className='opportunityScoutConfigModalBody'>
          <div className='opportunityScoutConfigHero'>
            <div>
              <p className='opportunityScoutEyebrow'>Reusable Search Setup</p>
              <h3>Choose the opportunities Ubunifu should keep discovering.</h3>
              <p>
                Save this once and the same categories and quality settings will be reused for manual and scheduled scans.
              </p>
            </div>
            <div className='opportunityScoutConfigHeroBadges'>
              <span>Web: Monday 9:00 AM</span>
              <span>LinkedIn: Monday 10:00 AM</span>
              <span>Stored in Firestore</span>
            </div>
          </div>

          <div className='growthScoutConfigLayout'>
            <section className='opportunityScoutConfigSection'>
              <div className='opportunityScoutConfigSectionHead'>
                <h4>Opportunity categories</h4>
                <p>Select the growth signals that should be included in each run.</p>
              </div>
              <div className='growthScoutCategoryOptions'>
                {GROWTH_CATEGORIES.map((item) => (
                  <label key={item.value} className={config.categories.includes(item.value) ? 'is-selected' : ''}>
                    <Form.Check
                      type='checkbox'
                      checked={config.categories.includes(item.value)}
                      onChange={() => handleCategoryToggle(item.value)}
                    />
                    <span><strong>{item.label}</strong><small>Scan open web and LinkedIn signals</small></span>
                  </label>
                ))}
              </div>
            </section>

            <section className='opportunityScoutConfigSection'>
              <div className='opportunityScoutConfigSectionHead'>
                <h4>Market and sources</h4>
                <p>Keep Kenya central while retaining useful East Africa opportunities.</p>
              </div>
              <div className='opportunityScoutConfigFieldGrid'>
                <Form.Group className='opportunityScoutConfigFieldCard'>
                  <Form.Label>Country focus</Form.Label>
                  <Form.Control
                    value={config.countryFocus}
                    onChange={(event) => setConfig((current) => ({...current, countryFocus: event.target.value}))}
                  />
                  <div className='opportunityScoutConfigHelp'>Used in every web and LinkedIn search.</div>
                </Form.Group>
                <div className='opportunityScoutConfigFieldCard growthScoutToggleCard'>
                  <Form.Check
                    type='switch'
                    label='Include East Africa'
                    checked={config.includeEastAfrica}
                    onChange={(event) => setConfig((current) => ({...current, includeEastAfrica: event.target.checked}))}
                  />
                  <small>Captures regional opportunities that remain strategically relevant to Kenya.</small>
                </div>
                <div className='opportunityScoutConfigFieldCard growthScoutToggleCard'>
                  <strong>Automatic Monday scans</strong>
                  <small>Open web runs at 9:00 AM and LinkedIn runs at 10:00 AM Nairobi time.</small>
                </div>
              </div>
            </section>

            <section className='opportunityScoutConfigSection growthScoutConfigFull'>
              <div className='opportunityScoutConfigSectionHead'>
                <h4>Volume and quality controls</h4>
                <p>Start conservatively, then increase volume once the results are consistently useful.</p>
              </div>
              <div className='growthScoutNumberGrid'>
                <Form.Group>
                  <Form.Label>Web results / category</Form.Label>
                  <Form.Control type='number' value='10' disabled />
                  <div className='opportunityScoutConfigHelp'>Fixed at 10 fetched web pages for each selected category.</div>
                </Form.Group>
                <Form.Group>
                  <Form.Label>LinkedIn pages / search</Form.Label>
                  <Form.Control type='number' value='1' disabled />
                  <div className='opportunityScoutConfigHelp'>Fixed at one page for each selected category.</div>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Minimum growth score</Form.Label>
                  <Form.Control type='number' min='0' max='100' value={config.minGrowthScore} onChange={(event) => setConfig((current) => ({...current, minGrowthScore: Number(event.target.value)}))} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Maximum new records / run</Form.Label>
                  <Form.Control type='number' min='4' max='100' value={config.maxNewPerRun} onChange={(event) => setConfig((current) => ({...current, maxNewPerRun: Number(event.target.value)}))} />
                </Form.Group>
              </div>
            </section>
          </div>
        </Modal.Body>
        <Modal.Footer className='opportunityScoutConfigModalFooter'>
          <Button variant='outline-secondary' onClick={() => setConfig(DEFAULT_GROWTH_CONFIG)} disabled={savingConfig}>Reset Defaults</Button>
          <Button variant='dark' onClick={handleSaveConfig} disabled={savingConfig}>
            {savingConfig ? <Spinner animation='border' size='sm' /> : null}
            <span className={savingConfig ? 'ms-2' : ''}>{savingConfig ? 'Saving...' : 'Save Configuration'}</span>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default GrowthScoutComp;
